<div class="page-head">
  <div>
    <h1>Businesses</h1>
    <div class="sub">
      @if (!isLoading) { {{ filteredBusinesses.length }} shown } @else { Loading… }
    </div>
  </div>
  @if (canCreateBusiness && !isLoading) {
    <div class="head-actions">
      <ng-container *staffCan="'Businesses.Create'"><button type="button" class="act" (click)="startCreateBusiness()">＋ New business</button></ng-container>
    </div>
  }
</div>

@if (!isLoading) {
  <div class="page-body">
    <div class="split">
      <div class="list-pane">
        <input class="inp" type="text" placeholder="Search businesses…"
          [(ngModel)]="searchTerm" (ngModelChange)="onSearch($event)" />

        <ul class="rows">
          @for (b of filteredBusinesses; track b.businessId) {
            <li class="row clickable"
              [class.active]="selectedBusiness?.businessId === b.businessId"
              (click)="onSelectBusiness(b)">
              <div class="row-main">
                <div class="row-title">
                  {{ b.name }}
                  @if (b.structureRole === 'headOffice') {
                    <span class="pill pill-sm" title="Head office of a business family">Head office</span>
                  } @else if (b.structureRole === 'branch') {
                    <span class="pill pill-sm pill-purple" [title]="'Branch of ' + (b.headOfficeName || b.parentBusinessId)">Branch</span>
                  }
                  @if (isUnderMaintenance(b)) {
                    <span class="pill pill-sm pill-red" title="Under maintenance: its tills and phones see the maintenance page">Maintenance</span>
                  }
                </div>
                @if (b.structureRole === 'branch') {
                  <div class="row-sub">Branch of {{ b.headOfficeName || b.parentBusinessId }}@if (b.branchCode) { · {{ b.branchCode }} }</div>
                }
                <!-- The owner is who the business is billed under, so a business without one has no plan. -->
                <div class="row-sub" [class.warn]="!b.ownerUserId">
                  @if (b.ownerUserId) { Owner: {{ b.ownerName || b.ownerUserId }} } @else { No owner }
                </div>
              </div>
              @if (isTestBusiness(b)) {
                <span class="test-badge" title="Platform test business">TEST</span>
              }
              @if (needsAttention(b)) {
                <span class="alert-badge" title="Business needs attention">!</span>
              }
            </li>
          }
          @if (!filteredBusinesses.length) {
            <li class="empty">No businesses match.</li>
          }
        </ul>
      </div>

      <div class="detail-pane">
        @if (createMessage) {
          <div class="note" [class.note-ok]="!createMessageIsWarning" [class.note-warn]="createMessageIsWarning">{{ createMessage }}</div>
        }

        @if (showCreateForm) {
          <div class="panel create-panel">
            <div class="panel-title">New business</div>
            @if (createError) { <div class="note note-err">{{ createError }}</div> }
            @if (createLookupsLoading) { <div class="note">Loading owners, business types and countries…</div> }

            <ng-container *staffCan="'Businesses.Create'"><form [formGroup]="createForm" (ngSubmit)="submitCreateBusiness()">
              <div class="form-grid">
                <label class="field">
                  <span>Owner *</span>
                  <select class="inp" formControlName="ownerUserId">
                    <option [ngValue]="null" disabled>Select owner…</option>
                    @for (u of users; track u.userId) {
                      <option [ngValue]="u.userId">{{ ownerOptionLabel(u) }}</option>
                    }
                  </select>
                  @if (invalid('ownerUserId')) { <small class="err">Choose an owner.</small> }
                  <small class="hint">Account holders only: the business runs on its owner's plan. To make someone else the owner, grant them AccountHolder (Plus) or MarketplaceAccountHolder (Marketplace) under Plus Users first.</small>
                </label>
                <label class="field">
                  <span>Business name *</span>
                  <input class="inp" type="text" formControlName="name" maxlength="100" />
                  @if (invalid('name')) { <small class="err">Business name is required.</small> }
                </label>
                <label class="field">
                  <span>Business type *</span>
                  <select class="inp" formControlName="businessTypeId">
                    <option [ngValue]="null" disabled>Select type…</option>
                    @for (t of businessTypes; track t.businessTypeId) {
                      <option [ngValue]="t.businessTypeId">{{ t.name }}</option>
                    }
                  </select>
                  @if (invalid('businessTypeId')) { <small class="err">Choose a business type.</small> }
                </label>
                <label class="field">
                  <span>Country *</span>
                  <select class="inp" formControlName="countryId">
                    <option [ngValue]="null" disabled>Select country…</option>
                    @for (c of countries; track c.countryId) {
                      <option [ngValue]="c.countryId">{{ c.countryName }}</option>
                    }
                  </select>
                  @if (invalid('countryId')) { <small class="err">Choose a country.</small> }
                </label>
                <label class="field">
                  <span>Address *</span>
                  <input class="inp" type="text" formControlName="address" />
                  @if (invalid('address')) { <small class="err">Address is required.</small> }
                </label>
                <label class="field">
                  <span>City *</span>
                  <input class="inp" type="text" formControlName="city" />
                  @if (invalid('city')) { <small class="err">City is required.</small> }
                </label>
                <label class="field">
                  <span>State / Region *</span>
                  <input class="inp" type="text" formControlName="state" />
                  @if (invalid('state')) { <small class="err">State / region is required.</small> }
                </label>
                <label class="field">
                  <span>Zip / Postal code</span>
                  <input class="inp" type="text" formControlName="zip" />
                </label>
                <label class="field">
                  <span>Customer service phone</span>
                  <input class="inp" type="text" formControlName="customerServicePhone" />
                </label>
              </div>

              <div class="form-actions">
                <button type="button" class="act-ghost" (click)="cancelCreateBusiness()" [disabled]="createSaving">Cancel</button>
                <button type="submit" class="act" [disabled]="createSaving || createLookupsLoading">
                  {{ createSaving ? 'Creating…' : 'Create business' }}
                </button>
              </div>
            </form></ng-container>
          </div>
        } @else if (selectedBusiness) {
          <!-- Platform test business: which business platform tooling (report self-tests
               today) exercises. Designating one changes nothing about the business itself. -->
          @if (canSetTestBusiness) {
            <div class="panel test-business">
              <div class="test-business-text">
                @if (isTestBusiness(selectedBusiness)) {
                  <strong>Platform test business.</strong>
                  Platform staff run report self-tests against this business.
                } @else {
                  <strong>Not the platform test business.</strong>
                  Platform tooling exercises
                  @if (testBusiness) { <em>{{ testBusiness.name || testBusiness.businessId }}</em> }
                  @else { no business yet }.
                }
              </div>
              <div class="test-business-actions">
                <!-- Follows the business profile's edit mode below, like every other change here. -->
                @if (isTestBusiness(selectedBusiness)) {
                  <ng-container *staffCan="'Platform.Settings.Manage'"><button type="button" class="act-ghost" [disabled]="testBusinessSaving || !businessEditing"
                    (click)="clearTestBusiness()">Clear</button></ng-container>
                } @else {
                  <ng-container *staffCan="'Platform.Settings.Manage'"><button type="button" class="act" [disabled]="testBusinessSaving || !businessEditing"
                    (click)="setAsTestBusiness(selectedBusiness)">
                    {{ testBusinessSaving ? 'Saving…' : 'Set as test business' }}
                  </button></ng-container>
                }
              </div>
            </div>
            @if (testBusinessError) {
              <div class="note note-err">{{ testBusinessError }}</div>
            }
          }
          <app-business-profile [businessId]="selectedBusiness.businessId"
            (editingChange)="businessEditing = $event"></app-business-profile>
        } @else {
          <div class="panel empty">Select a business to view its profile.</div>
        }
      </div>
    </div>
  </div>
} @else {
  <div class="loading">Loading businesses…</div>
}
